import React from 'react';
import { SimpleProductList } from 'src/components/SimpleProductList/SimpleProductList';
import { ProductItemBySku, ProductStatus, StockStatus } from 'src/types/global-types';
import { useQuery } from '@apollo/client';
import categoryProducts from 'src/queries/moreFromUpsellsCrossells.graphql';
import excludedProducts from 'src/queries/excludedProducts.graphql';
import camelcaseKeys from 'camelcase-keys';
import { MORE_FROM_BRAND_RESULTS, NUMBER_OF_SLIDES_TO_SHOW } from 'src/constants/product';

import classes from './ProductDetailRelatedProducts.scss';

type Props = {
    crosssellProducts?: ProductItemBySku[];
    upsellProducts?: ProductItemBySku[];
    fromBrand: { gender?: number; brandId?: number; productId: number; brandName: string };
    additionalCategoryId?: number;
    excludedProductsCategoryId?: number;
    moreFromBrand?: ProductItemBySku[];
};

type RelatedProduct = ProductItemBySku & {
    stock_status?: string;
    stockStatus?: string;
};

const isEnabledInStockProduct = (product: RelatedProduct) =>
    product.status === ProductStatus.enable &&
    (product.stockStatus === StockStatus.InStock ||
        product.stockStatus === 'IN_STOCK' ||
        product.stock_status === 'IN_STOCK');

const hasEnabledInStockProduct = (products?: RelatedProduct[]) =>
    Boolean(products?.length && products.some(isEnabledInStockProduct));

export const ProductDetailRelatedProducts: React.FC<Props> = ({
    crosssellProducts,
    upsellProducts,
    fromBrand,
    additionalCategoryId,
    excludedProductsCategoryId,
    moreFromBrand,
}) => {
    const { data: dataCategoryProducts } = useQuery(categoryProducts, {
        variables: {
            pageSize: 15,
            currentPage: 1,
            productFilters: {
                category_id: { eq: String(additionalCategoryId) },
            },
            includeProducts: true,
        },
        skip: !additionalCategoryId,
    });

    const { data: dataExcludedProducts } = useQuery(excludedProducts, {
        variables: {
            pageSize: 300,
            productFilters: {
                category_id: { eq: String(excludedProductsCategoryId) },
            },
            includeProducts: true,
        },
        skip: !excludedProductsCategoryId,
    });

    const filteredCrossellProducts =
        crosssellProducts &&
        crosssellProducts?.filter((item) => {
            const isContainedInExcludedProducts = Boolean(
                dataExcludedProducts &&
                dataExcludedProducts?.excludedProducts.items.find((excludedProduct) => excludedProduct.id === item.id),
            );

            if (isContainedInExcludedProducts) return null;

            return true;
        });

    const filteredUpsellProducts =
        upsellProducts &&
        upsellProducts?.filter((item) => {
            const isContainedInExcludedProducts = Boolean(
                dataExcludedProducts &&
                dataExcludedProducts?.excludedProducts.items.find((excludedProduct) => excludedProduct.id === item.id),
            );

            if (isContainedInExcludedProducts) return null;

            return true;
        });

    const computedCrossellProducts =
        crosssellProducts && filteredCrossellProducts && filteredCrossellProducts?.length < 3 && dataCategoryProducts
            ? [
                  ...filteredCrossellProducts,
                  ...camelcaseKeys(
                      dataCategoryProducts.categoryProducts.items
                          .filter(Boolean)
                          .slice(0, MORE_FROM_BRAND_RESULTS - crosssellProducts?.length),
                      { deep: true },
                  ),
              ]
            : filteredCrossellProducts;

    const computedUpsellProducts =
        upsellProducts && filteredUpsellProducts && filteredUpsellProducts?.length < 3 && dataCategoryProducts
            ? [
                  ...filteredUpsellProducts,
                  ...camelcaseKeys(
                      dataCategoryProducts.categoryProducts.items
                          .filter(Boolean)
                          .slice(
                              MORE_FROM_BRAND_RESULTS,
                              MORE_FROM_BRAND_RESULTS + (MORE_FROM_BRAND_RESULTS - upsellProducts?.length),
                          ),
                      { deep: true },
                  ),
              ]
            : filteredUpsellProducts;

    const moveLastElementsToFront = (arr) => {
        const arrayLength = arr.length;
        const lastElements = arr.slice(arrayLength - NUMBER_OF_SLIDES_TO_SHOW, arrayLength);
        const remainingElements = arr.slice(0, arrayLength - NUMBER_OF_SLIDES_TO_SHOW);
        return [...lastElements, ...remainingElements];
    };

    const showUpsellProducts = hasEnabledInStockProduct(computedUpsellProducts);
    const showCrossellProducts = hasEnabledInStockProduct(computedCrossellProducts);
    const showMoreFromBrand = hasEnabledInStockProduct(moreFromBrand);

    return (
        <div className={classes.container}>
            {showUpsellProducts && (
                <>
                    <div className={classes.carousel}>
                        <h2>You might also like</h2>
                        <SimpleProductList
                            products={moveLastElementsToFront(computedUpsellProducts)}
                            display="carousel"
                        />
                    </div>
                    {showCrossellProducts || showMoreFromBrand ? <div className={classes.border} /> : null}
                </>
            )}
            {showCrossellProducts && (
                <>
                    <div className={classes.carousel}>
                        <h2>Frequently bought together</h2>
                        <SimpleProductList
                            products={moveLastElementsToFront(computedCrossellProducts)}
                            display="carousel"
                        />
                    </div>
                    {showMoreFromBrand && <div className={classes.border} />}
                </>
            )}
            {showMoreFromBrand && (
                <div className={classes.carousel}>
                    <h2>
                        More <span>from</span> {fromBrand.brandName}
                    </h2>
                    <SimpleProductList
                        products={moveLastElementsToFront(
                            camelcaseKeys((moreFromBrand ?? []) as any, {
                                deep: true,
                            }) as unknown as ProductItemBySku[],
                        )}
                        display={(moreFromBrand?.length ?? 0) <= 2 ? 'flexLeft' : 'carousel'}
                    />
                </div>
            )}
        </div>
    );
};
